// What the create surface is being asked to author.
//
// One flat union rather than a channel plus an `mms` flag. The flag version
// could represent `email` + `mms`, which means nothing, and it took two
// controls to express one decision. This also mirrors the `?type=` search param
// exactly, so the URL and the surface state cannot drift.
//
// Lives apart from CreateEmailSurface so the route and its tests can use it
// without pulling in the whole surface.
export type CreateType = 'email' | 'sms' | 'mms';

/** Which backend the submit routes to. MMS is an SMS with an image attached. */
export type CreateChannel = 'email' | 'sms';

export function channelForType(type: CreateType): CreateChannel {
  return type === 'email' ? 'email' : 'sms';
}

export function isCreateType(value: unknown): value is CreateType {
  return value === 'email' || value === 'sms' || value === 'mms';
}

/**
 * Narrow a requested type to what this surface is allowed to author.
 *
 * Deliberately not about the connected ESP. Whether a message can be written
 * and whether it can be sent from here are different questions, and an ESP is
 * only needed for the second: a client with nothing connected can still author
 * SMS and MMS, and export refuses at the point of sending if the provider
 * cannot carry it. Today both flags are the one SMS/MMS feature flag; the pair
 * survives so MMS can be narrowed on its own without reshaping every caller.
 *
 * Degrades rather than refuses: a `?type=mms` where MMS is not on offer is
 * still a worthwhile SMS, and a shared link should not dead-end because the
 * recipient's org differs from the sender's.
 */
export function resolveCreateType(
  requested: CreateType,
  caps: { smsEnabled: boolean; mmsEnabled: boolean },
): CreateType {
  if (requested === 'email') return 'email';
  if (!caps.smsEnabled) return 'email';
  if (requested === 'mms' && !caps.mmsEnabled) return 'sms';
  return requested;
}
